import { pairUnambiguous } from './pairClosest'
import type { ActivityEntry, ActivityKind, TransferPartner } from './types'

// The same window lukita's chat matcher uses (Agent::InternalTransferMatcher::MAX_DAYS_APART).
const TRANSFER_MAX_DAYS_APART = 3

export const MONEY_MOVEMENT_KINDS: ReadonlySet<ActivityKind> = new Set([
  'deposit',
  'withdrawal',
  'transfer_in',
  'transfer_out',
  'contribution',
  'distribution',
])

const isTransferCandidate = (entry: ActivityEntry) =>
  MONEY_MOVEMENT_KINDS.has(entry.kind) &&
  entry.amount != null &&
  entry.amount !== 0 &&
  entry.date !== ''

const sizeInCents = (entry: ActivityEntry) =>
  Math.round(Math.abs(entry.amount ?? 0) * 100)

const partnerOf = (entry: ActivityEntry): TransferPartner => ({
  entryId: entry.id,
  accountId: entry.accountId,
  accountName: entry.accountName,
  date: entry.date,
  sourcePage: entry.sourcePage,
  statementFileUrl: entry.statementFileUrl,
})

const bySize = (entries: ActivityEntry[]) => {
  const groups = new Map<
    number,
    { senders: ActivityEntry[]; receivers: ActivityEntry[] }
  >()
  for (const entry of entries) {
    const size = sizeInCents(entry)
    const group = groups.get(size) ?? { senders: [], receivers: [] }
    if ((entry.amount ?? 0) < 0) group.senders.push(entry)
    else group.receivers.push(entry)
    groups.set(size, group)
  }
  return groups
}

// Money that left one account in the case and reached another: the same amount, opposite
// directions, at most 3 days apart. No text is read. A row with two equally close partners is
// ambiguous and stays an unpaired, counted transfer.
export const pairTransfers = (entries: ActivityEntry[]): ActivityEntry[] => {
  const pairedById = new Map<string, ActivityEntry>()
  for (const { senders, receivers } of bySize(
    entries.filter(isTransferCandidate),
  ).values()) {
    const pairs = pairUnambiguous(senders, receivers, {
      maxDaysApart: TRANSFER_MAX_DAYS_APART,
      canPair: (sender, receiver) => sender.accountId !== receiver.accountId,
    })
    for (const [sender, receiver] of pairs) {
      pairedById.set(sender.id, {
        ...sender,
        kind: 'transfer_out',
        pairing: { role: 'sender', partner: partnerOf(receiver) },
      })
      pairedById.set(receiver.id, {
        ...receiver,
        kind: 'transfer_in',
        pairing: { role: 'receiver', partner: partnerOf(sender) },
      })
    }
  }
  if (pairedById.size === 0) return entries
  return entries.map((entry) => pairedById.get(entry.id) ?? entry)
}
